'use client';
import React, { useEffect, ReactNode } from 'react';
import { useAuthenticationPopUp } from '../contexts/authentication-popup-context';
import axios from './axios';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getCookie } from 'cookies-next';
import { clearAuthIgnoreCookie, clearUserCookies, deleteExpiredToken, getAccessToken, setAuthIgnoreCookie } from '../helpers/cookiesMethod';
import { isAppBridge, clearAppUserStorage } from '../helpers/appStorageMethod';
import { useRouter, usePathname } from 'next/navigation';
import utilsService from '../helpers/utilsService';

interface AxiosErrorHandlerProps {
    children: ReactNode;
}

const COOKIE_NAME = 'auth_ignore';
const title500Error = '네트워크 에러가 발생하였습니다.<br /> <br />고객센터에 연락해주세요.';
const AxiosErrorHandler: React.FC<AxiosErrorHandlerProps> = ({ children }) => {
    const { confirmPopUp } = useAuthenticationPopUp();
    const router = useRouter();
    const pathname = usePathname();

    const handleCancel = async () => {
        clearUserCookies();
        deleteExpiredToken();
        if (isAppBridge()) {
            await clearAppUserStorage();
        }
        setAuthIgnoreCookie(COOKIE_NAME, 1);
        confirmPopUp.close();
    };

    const handleAccept = async () => {
        clearAuthIgnoreCookie(COOKIE_NAME);
        clearUserCookies();
        deleteExpiredToken();
        if (isAppBridge()) {
            await clearAppUserStorage();
        }
        // U6-6 ③: 세션 만료로 튕겨도 같은 화면으로 돌아온다.
        router.push(utilsService.buildLoginUrl(pathname));
        confirmPopUp.close();
    };

    const handleCancelWithLogout = async () => {
        setAuthIgnoreCookie(COOKIE_NAME, 1);
        clearUserCookies();
        deleteExpiredToken();
        if (isAppBridge()) {
            await clearAppUserStorage();
        }
        confirmPopUp.close();
        // window.location.reload()
        router.replace('/login');
    };

    const handleClosePopup = () => {
        confirmPopUp.close();
    };

    useEffect(() => {
        const responseInterceptor = axios.interceptors.response.use(
            (response: AxiosResponse) => {
                return response;
            },
            async (error: AxiosError | any) => {
                const originalRequest = error?.config as AxiosRequestConfig;
                if (error?.status === 500 && error?.code === 'ERR_NETWORK' && error.code !== 'ERR_CANCELED') {
                    //handle only Error server
                    //detect avoid canceled  => but validate error undefined
                    //detect 500 just close popup and not redirect to login
                    const errorUrl = originalRequest?.url || error?.response?.config?.url || 'Unknown URL';
                    const errorMessage = error?.message || 'No message';
                    console.error(`[AxiosHandler] Network Error 500: ${errorUrl} - ${errorMessage}`);
                    console.error('[AxiosHandler] Full Error Object:', {
                        code: error?.code,
                        status: error?.status,
                        url: errorUrl,
                        message: errorMessage,
                        response: error?.response ? {
                            status: error.response.status,
                            data: error.response.data,
                            headers: error.response.headers
                        } : 'No response body',
                        request: error?.request ? 'Request object exists' : 'No request object'
                    });

                    // Ignore modal for certain non-critical/background endpoints or when the user is on chat-related pages
                    const isChatPage = typeof window !== 'undefined' && 
                        (window.location.pathname.includes('/chat') || window.location.pathname.includes('/chat-ios'));

                    const isIgnoreUrl = 
                        errorUrl.includes('user/fcm') || 
                        errorUrl.includes('/chat/active-status') || 
                        errorUrl.includes('/chat/unread-count') || 
                        errorUrl.includes('/chat/conversations') || 
                        errorUrl.includes('/user/notifications/check');

                    if (isIgnoreUrl || isChatPage) {
                        return Promise.reject(error);
                    }

                    confirmPopUp.open({ acceptActions: handleClosePopup, showCloseButton: false, title: title500Error });
                    return Promise.reject(error);
                }
                // axios.ts 가 넘기는 error 는 이미 서버 응답 바디 자체(평탄한 {msg, is_deleted, ...})다.
                // 예전엔 error.data.msg 를 읽어 status 가 있어도 title 이 안 뜨고(410),
                // is_deleted 조건이 항상 false 라 403 분기가 아예 진입 못 했다(2026-09-29 수정).
                //
                // 이 인터셉터의 팝업 분기들은 전부 reject 없이 `return;`(=undefined 로 resolve)만
                // 하고 있었다 — axios 인터셉터 체인에서 onRejected 가 reject 하지 않고 값을 반환하면
                // 그 체인은 fulfilled 로 바뀐다. 그러면 호출부(fetchWrapper._get 등)가 `response.data`
                // 를 읽다가 "Cannot read properties of undefined (reading 'data')" 로 터진다. 이전엔
                // error.status 가 항상 undefined 라 이 분기 자체에 도달한 적이 없어 안 드러났을
                // 뿐이다 — status 를 고치면서 실제로 밟히게 돼 stage 재현 중 발견(2026-09-29).
                if (error?.status && error.status === 410) {
                    confirmPopUp.open({ acceptActions: handleAccept, showCloseButton: false, title: error?.msg });
                    return Promise.reject(error);
                }
                if (error?.status && error.status === 403 && error?.is_deleted) {
                    confirmPopUp.open({ acceptActions: handleAccept, showCloseButton: false, title: error?.msg });
                    return Promise.reject(error);
                }

                if (error.status && error.status === 401) {
                    const isRefreshTokenRequest = originalRequest.url?.includes('/auth/refresh-token');

                    // On App Bridge, we ignore 401 popups because the app will provide a new token via GlobalAppBridgeHandler
                    // or the native app will handle the login state itself.
                    if (isAppBridge()) {
                        return Promise.reject(error);
                    }

                    if (isRefreshTokenRequest) {
                        // Refresh token failed -> force logout/login popup
                        confirmPopUp.open({ acceptActions: handleAccept, cancelActions: handleCancel, showCloseButton: false });
                        return Promise.reject(error);
                    }

                    // If it's not a refresh token request, the axios.ts interceptor should have handled it.
                    // If we are here, it means either:
                    // 1. Refresh was skipped (no token)
                    // 2. Refresh was already tried and failed (_retry is true)
                    // 3. Request was a logout request

                    if (originalRequest.url?.includes('/auth/logout')) {
                        confirmPopUp.open({ acceptActions: handleAccept, cancelActions: handleCancelWithLogout });
                        return Promise.reject(error);
                    }

                    // TH2: Chỉ logout khi đã thử Refresh (_retry=true) VÀ vẫn không có _refresh token
                    // Nếu chỉ là 401 thông thường, để axios.ts interceptor tự xử lý Refresh + retry.
                    if ((originalRequest as any)._retry && !getCookie('_refresh')) {
                        if (!getCookie(COOKIE_NAME)) {
                            confirmPopUp.open({ acceptActions: handleAccept, cancelActions: handleCancel });
                            return Promise.reject(error);
                        }
                    }

                    // Otherwise, it might be an in-progress refresh or a race condition, 
                    // we let the axios.ts interceptor handle the retry.
                }
                return Promise.reject(error);
            }
        );
        return () => {
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [confirmPopUp]);

    return children;
};

export default AxiosErrorHandler;
